import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Employees.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(String(s).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const SALUTATIONS = ['', 'Mr', 'Mrs', 'Ms', 'Dr'];

const emptyForm = {
  salutation: '', first_name: '', last_name: '', job_code: '',
  primary_phone: '', primary_email: '', secondary_email: '',
  dob: '', status: 'A', user_id: '',
};

const Employees = () => {
  const { user } = useContext(AuthContext);
  const hasPerm = (p) => user?.permissions?.some((x) => x.name === p) ?? false;

  const canView   = hasPerm('employees.view');
  const canCreate = hasPerm('employees.create');
  const canUpdate = hasPerm('employees.update');

  const [employees, setEmployees]   = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);
  const [modalError, setModalError] = useState('');
  const [usersLov, setUsersLov]     = useState([]);

  const PER_PAGE = 20;

  useEffect(() => { if (canView) load(); }, [page, search, statusFilter]); // eslint-disable-line
  useEffect(() => { loadUsersLov(); }, []); // eslint-disable-line

  const loadUsersLov = async () => {
    try {
      const res = await axios.get(`${API}/commonLOVs/lhusers`, { headers: authHdr(user.token) });
      setUsersLov(res.data || []);
    } catch { setUsersLov([]); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API}/employees?${params}`, { headers: authHdr(user.token) });
      setEmployees(res.data.employees || []);
      setTotal(res.data.pagination?.total || 0);
      setTotalPages(res.data.pagination?.totalPages || 1);
      setError('');
    } catch {
      setError('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      salutation:       emp.salutation || '',
      first_name:       emp.first_name || '',
      last_name:        emp.last_name || '',
      job_code:         emp.job_code || '',
      primary_phone:    emp.primary_phone || '',
      primary_email:    emp.primary_email || '',
      secondary_email:  emp.secondary_email || '',
      dob:              emp.dob ? String(emp.dob).slice(0, 10) : '',
      status:           emp.status || 'A',
      user_id:          emp.user_id || '',
    });
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { setModalError('First name is required.'); return; }
    if (!form.job_code.trim())   { setModalError('Job code is required.'); return; }
    setSaving(true); setModalError('');
    try {
      if (editing) {
        await axios.put(`${API}/employees/${editing.id}`, form, { headers: authHdr(user.token) });
        setSuccess('Employee updated.');
      } else {
        await axios.post(`${API}/employees`, form, { headers: authHdr(user.token) });
        setSuccess('Employee created.');
      }
      closeModal();
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  if (!canView) {
    return <div className="emp-page"><p className="emp-no-perm">You do not have permission to view employees.</p></div>;
  }

  return (
    <div className="emp-page">
      <div className="emp-header">
        <div>
          <h1 className="emp-title">Employees</h1>
          <p className="emp-sub">{total} employee{total !== 1 ? 's' : ''}</p>
        </div>
        {canCreate && (
          <button className="emp-btn-add" onClick={openAdd}>+ Add Employee</button>
        )}
      </div>

      {error   && <div className="emp-alert emp-alert-error">{error}</div>}
      {success && <div className="emp-alert emp-alert-success">{success}</div>}

      <div className="emp-controls">
        <div className="emp-search-box">
          <span className="emp-search-icon">🔍</span>
          <input
            className="emp-search-input"
            placeholder="Search by name, phone, email, job code…"
            value={search}
            onChange={handleSearch}
          />
        </div>
        <select
          className="emp-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="A">Active</option>
          <option value="I">Inactive</option>
        </select>
      </div>

      <div className="emp-table-wrap">
        <table className="emp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Job Code</th>
              <th>Phone</th>
              <th>Email</th>
              <th>DOB</th>
              <th>Linked User</th>
              <th>Status</th>
              {canUpdate && <th></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canUpdate ? 8 : 7} className="emp-loading">Loading…</td></tr>
            ) : employees.length === 0 ? (
              <tr><td colSpan={canUpdate ? 8 : 7} className="emp-empty">No employees found.</td></tr>
            ) : employees.map((emp) => (
              <tr key={emp.id}>
                <td>
                  <div className="emp-name">
                    <div className="emp-avatar">
                      {(emp.first_name?.[0] || '?').toUpperCase()}
                    </div>
                    <span>{[emp.salutation, emp.first_name, emp.last_name].filter(Boolean).join(' ')}</span>
                  </div>
                </td>
                <td><span className="emp-job-badge">{emp.job_code}</span></td>
                <td>{emp.primary_phone || '—'}</td>
                <td>{emp.primary_email || '—'}</td>
                <td>{fmtDate(emp.dob)}</td>
                <td>{emp.user_name || '—'}</td>
                <td>
                  <span className={`emp-status ${emp.status === 'A' ? 'emp-status-active' : 'emp-status-inactive'}`}>
                    {emp.status === 'A' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {canUpdate && (
                  <td>
                    <button className="emp-btn-edit" onClick={() => openEdit(emp)}>Edit</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="emp-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content emp-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Employee' : 'Add Employee'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-body">
                {modalError && <div className="emp-modal-error">{modalError}</div>}
                <div className="form-group">
                  <label>Salutation</label>
                  <select className="form-select" value={form.salutation} onChange={(e) => set('salutation', e.target.value)}>
                    {SALUTATIONS.map((s) => <option key={s} value={s}>{s || '—'}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>First Name *</label>
                  <input className="form-input" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input className="form-input" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Job Code *</label>
                  <input className="form-input" value={form.job_code} onChange={(e) => set('job_code', e.target.value.toUpperCase())} required />
                </div>
                <div className="form-group">
                  <label>Primary Phone</label>
                  <input className="form-input" type="tel" value={form.primary_phone} onChange={(e) => set('primary_phone', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Primary Email</label>
                  <input className="form-input" type="email" value={form.primary_email} onChange={(e) => set('primary_email', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Secondary Email</label>
                  <input className="form-input" type="email" value={form.secondary_email} onChange={(e) => set('secondary_email', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input className="form-input" type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-select" value={form.status} onChange={(e) => set('status', e.target.value)}>
                    <option value="A">Active</option>
                    <option value="I">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Linked User</label>
                  <select className="form-select" value={form.user_id} onChange={(e) => set('user_id', e.target.value)}>
                    <option value="">— None —</option>
                    {usersLov.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
